import { useState } from 'react'
import { Terminal, ExternalLink, Github, Radio, Lock, Globe } from 'lucide-react'
import { useArticles } from '../hooks/useArticles'
import type { Article } from '../types/blog'

/* ─────────────────────────────────────────
   Helpers
   ───────────────────────────────────────── */
function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diff = Math.max(0, now - then)

  const minutes = Math.floor(diff / 60_000)
  const hours   = Math.floor(diff / 3_600_000)
  const days    = Math.floor(diff / 86_400_000)

  if (minutes < 1)  return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24)   return `${hours}h ago`
  if (days < 30)    return `${days}d ago`
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const BADGE: Record<string, string> = {
  critical: 'badge-critical text-white font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5',
  high:     'badge-high     text-white font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5',
  medium:   'badge-medium   text-white font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5',
  low:      'badge-low      font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5',
}

/* ─────────────────────────────────────────
   Page
   ───────────────────────────────────────── */
export default function Comms() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const { articles: allArticles, loading: allLoading, error: allError } = useArticles({ limit: 6 })
  const { articles: privacyArticles, loading: privacyLoading, error: privacyError } = useArticles({
    category: 'privacy',
    limit: 6,
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (email.trim()) {
      setSubmitted(true)
    }
  }

  return (
    <div className="bg-terminal-bg min-h-screen">
      <div className="container mx-auto px-4 max-w-7xl py-10">

        {/* ── Header ── */}
        <div className="mb-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-military mb-2">
            ★ &nbsp; Intelligence Network Division
          </p>
          <h1 className="font-display font-bold text-4xl md:text-5xl text-white uppercase tracking-wider mb-3">
            Secure Comms
          </h1>
          <p className="text-text-gray font-mono text-sm leading-relaxed max-w-2xl">
            Join the intelligence network. Receive encrypted threat briefings, connect with
            field operatives, and monitor active signals from the global security community.
          </p>
        </div>

        {/* ── Section divider ── */}
        <div className="flex items-center gap-4 mb-8">
          <div className="h-px flex-1 bg-border-gray/50" />
          <div className="flex items-center gap-2">
            <Radio className="text-military" size={16} />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-text-gray">
              Intelligence Network
            </span>
          </div>
          <div className="h-px flex-1 bg-border-gray/50" />
        </div>

        {/* ── Two-column: Feed + Join ── */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">

          {/* LEFT: Latest feed */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Terminal className="text-kgb-red" size={14} />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-text-gray">
                Live Signal Feed
              </span>
            </div>

            {allLoading && (
              <div className="terminal-window p-4">
                <p className="font-mono text-status-secure text-xs animate-pulse">
                  $ SCANNING INTEL FEEDS..._
                </p>
              </div>
            )}

            {allError && (
              <div className="terminal-window p-4">
                <p className="font-mono text-kgb-red text-xs">[ERROR] {allError}</p>
              </div>
            )}

            {!allLoading && !allError && (
              <div className="flex flex-col gap-0 border border-border-gray">
                {allArticles.map((article, i) => (
                  <NewsTicker
                    key={article.id}
                    article={article}
                    isLast={i === allArticles.length - 1}
                  />
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: Join + contact */}
          <div className="flex flex-col gap-6">

            {/* Join the network */}
            <div
              className="p-6 border border-kgb-red/30 relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(220,20,60,0.08) 0%, transparent 100%)',
              }}
            >
              <div className="absolute top-0 left-0 w-12 h-0.5 bg-kgb-red" />
              <div className="absolute top-0 left-0 w-0.5 h-12 bg-kgb-red" />

              <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-kgb-red mb-3">
                ★ &nbsp; Join the Network
              </p>
              <h2 className="font-display font-bold text-xl text-white uppercase tracking-wide mb-2">
                Encrypted Briefings
              </h2>
              <p className="font-mono text-xs text-text-gray mb-5 leading-relaxed">
                Nightly threat intelligence delivered to your secure terminal.
                Zero noise. Maximum signal.
              </p>

              {submitted ? (
                <div className="terminal-window p-4">
                  <p className="font-mono text-status-secure text-xs">
                    [OK] TRANSMISSION RECEIVED — Welcome, operative.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="operative@secure.channel"
                    required
                    className="bg-black text-white px-4 py-3 border border-border-gray focus:border-kgb-red focus:outline-none font-mono text-sm placeholder:text-text-gray/50"
                  />
                  <button type="submit" className="btn-mission flex items-center justify-center gap-2">
                    <Lock size={12} />
                    ESTABLISH SECURE LINK
                  </button>
                </form>
              )}
            </div>

            {/* Social links */}
            <div className="p-5 border border-border-gray bg-panel-bg">
              <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-text-gray mb-4">
                Field Contact
              </p>
              <div className="flex flex-col gap-3">
                <a
                  href="https://github.com/teddykgb"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 font-mono text-xs text-text-gray hover:text-white transition-colors group"
                >
                  <Github size={14} className="text-text-gray group-hover:text-white transition-colors" />
                  github.com/teddykgb
                  <ExternalLink size={10} className="ml-auto opacity-50" />
                </a>
                <a
                  href="https://twitter.com/teddykgb"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 font-mono text-xs text-text-gray hover:text-white transition-colors group"
                >
                  <Globe size={14} className="text-text-gray group-hover:text-white transition-colors" />
                  @teddykgb
                  <ExternalLink size={10} className="ml-auto opacity-50" />
                </a>
              </div>
            </div>

            {/* Contact */}
            <div className="p-5 border border-border-gray bg-panel-bg">
              <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-text-gray mb-3">
                Secure Contact
              </p>
              <p className="font-mono text-xs text-text-gray leading-relaxed">
                For classified correspondence and collaboration inquiries:
              </p>
              <p className="font-mono text-xs text-military mt-2">
                contact@teddy-kgb.com
              </p>
            </div>
          </div>
        </div>

        {/* ── Signals from the Field ── */}
        <div className="flex items-center gap-4 mb-6">
          <div className="h-px flex-1 bg-border-gray/50" />
          <div className="flex items-center gap-2">
            <Lock className="text-kgb-red" size={16} />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-text-gray">
              Signals from the Field
            </span>
          </div>
          <div className="h-px flex-1 bg-border-gray/50" />
        </div>

        {privacyLoading && (
          <div className="terminal-window p-6 text-center mb-10">
            <p className="font-mono text-status-secure text-sm animate-pulse">
              $ SCANNING INTEL FEEDS..._
            </p>
          </div>
        )}

        {privacyError && (
          <div className="terminal-window p-6 text-center mb-10">
            <p className="font-mono text-kgb-red text-sm">[ERROR] {privacyError}</p>
          </div>
        )}

        {!privacyLoading && !privacyError && (
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            {privacyArticles.map(article => (
              <PrivacyCard key={article.id} article={article} />
            ))}
          </div>
        )}

        {/* ── Terminal status block ── */}
        <div className="terminal-window">
          <div className="terminal-header">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-kgb-red opacity-80" />
              <div className="w-2.5 h-2.5 rounded-full bg-status-warning opacity-80" />
              <div className="w-2.5 h-2.5 rounded-full bg-status-online opacity-80" />
            </div>
            <span className="ml-3 text-[11px] font-mono text-text-gray tracking-wider">
              SECURE_CHANNEL_STATUS.sh
            </span>
          </div>
          <div className="terminal-body text-sm space-y-1.5 p-5">
            <p className="text-text-gray">$ connecting to secure channel...</p>
            <p><span className="text-status-secure">[OK]</span> SIGNALS ACTIVE</p>
            <p><span className="text-status-secure">[OK]</span> 8 INTEL SOURCES MONITORED</p>
            <p><span className="text-status-secure">[OK]</span> NIGHTLY SCRAPE: 02:00 UTC</p>
            <p><span className="text-status-secure">[OK]</span> ENCRYPTION: AES-256-GCM</p>
            <p><span className="text-status-secure">[OK]</span> CHANNEL INTEGRITY: VERIFIED</p>
            <p className="flex items-center gap-1 mt-2">
              <span className="text-text-gray">$</span>
              <span className="w-2 h-4 bg-status-secure animate-blink inline-block" />
            </p>
          </div>
        </div>

      </div>
    </div>
  )
}

/* ─────────────────────────────────────────
   News Ticker Row
   ───────────────────────────────────────── */
function NewsTicker({ article, isLast }: { article: Article; isLast: boolean }) {
  return (
    <a
      href={article.source_url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-3 px-4 py-3 bg-panel-bg hover:bg-black/60 transition-colors group ${
        !isLast ? 'border-b border-border-gray/50' : ''
      }`}
    >
      <span className={BADGE[article.threat_level]}>
        {article.threat_level.slice(0, 4)}
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-mono text-[11px] text-text-light group-hover:text-white transition-colors truncate">
          {article.title}
        </p>
        <p className="font-mono text-[10px] text-text-gray/60 uppercase tracking-wide mt-0.5">
          {article.source_name} · {timeAgo(article.published_at)}
        </p>
      </div>
      <ExternalLink size={10} className="text-text-gray/40 group-hover:text-kgb-red transition-colors flex-shrink-0" />
    </a>
  )
}

/* ─────────────────────────────────────────
   Privacy Card (3-col grid)
   ───────────────────────────────────────── */
function PrivacyCard({ article }: { article: Article }) {
  return (
    <a
      href={article.source_url}
      target="_blank"
      rel="noopener noreferrer"
      className="block"
    >
      <div className="bracket-card bg-panel-bg border border-border-gray hover:border-military/50 transition-all group h-full p-4">
        <div className="flex items-start justify-between mb-3">
          <span className={BADGE[article.threat_level]}>
            {article.threat_level}
          </span>
          <span className="font-mono text-[9px] uppercase tracking-wide text-text-gray/50">
            {article.source_name}
          </span>
        </div>
        <h3 className="font-display font-bold text-white text-sm leading-snug mb-2 group-hover:text-military transition-colors line-clamp-3">
          {article.title}
        </h3>
        {article.excerpt && (
          <p className="font-mono text-[10px] text-text-gray leading-relaxed line-clamp-2 mb-3">
            {article.excerpt}
          </p>
        )}
        <div className="flex items-center justify-between pt-2 border-t border-border-gray/40 mt-auto">
          <span className="font-mono text-[10px] text-text-gray/50">
            {formatDate(article.published_at)}
          </span>
          <span className="font-mono text-[10px] text-military/70 group-hover:text-military transition-colors uppercase tracking-widest">
            Read →
          </span>
        </div>
      </div>
    </a>
  )
}
